<script setup lang="ts">
import { TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import {
  SOURCE_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  cancelOrder,
  closeOrder,
  getOrder,
  releaseOrder,
  shipOrder,
  type Detail,
  type OutboundSource,
  type OutboundStatus,
} from '../api/outbound'
import { createWave } from '../api/waves'

const route = useRoute()
const router = useRouter()

const detail = ref<Detail | null>(null)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')
const busy = ref(false)

const orderId = computed(() => Number(route.params.id))
const order = computed(() => detail.value?.order ?? null)

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    detail.value = await getOrder(orderId.value)
    notice.value = noticeText
  } catch (e) {
    error.value = e
    detail.value = null
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})

function back(): void {
  void router.push({ name: 'outbound' })
}

/** 释放后的单生成波次：系统按效期/入库时间分配拣货任务并锁库存，成功后直接跳到波次页 */
async function makeWave(): Promise<void> {
  notice.value = ''
  error.value = null
  busy.value = true
  try {
    const w = await createWave({ order_ids: [orderId.value] })
    await router.push({ name: 'wave-detail', params: { id: w.id } })
  } catch (e) {
    error.value = e
  } finally {
    busy.value = false
  }
}

async function act(action: 'release' | 'cancel' | 'close' | 'ship', text: string): Promise<void> {
  notice.value = ''
  error.value = null
  busy.value = true
  try {
    if (action === 'release') await releaseOrder(orderId.value)
    else if (action === 'cancel') await cancelOrder(orderId.value)
    else if (action === 'close') await closeOrder(orderId.value)
    else await shipOrder(orderId.value)
    await load(text)
  } catch (e) {
    error.value = e
  } finally {
    busy.value = false
  }
}

const canEdit = computed(() => order.value?.status === 'draft')
const canWave = computed(() => order.value?.status === 'released')
const canCancel = computed(() =>
  order.value ? order.value.status === 'draft' || order.value.status === 'released' : false,
)
const canShip = computed(() => order.value?.status === 'picked')
const canClose = computed(() =>
  order.value ? order.value.status === 'picked' || order.value.status === 'shipped' : false,
)

/** 待拣数量合计：全部拣完才谈得上发货 */
const pendingTotal = computed(() =>
  (detail.value?.items ?? []).reduce((sum, item) => sum + (item.qty - item.picked_qty), 0),
)
</script>

<template>
  <div class="yc-outbd" v-loading="loading">
    <header class="yc-outbd__bar">
      <el-button @click="back">返回列表</el-button>
      <template v-if="order">
        <span class="yc-outbd__docno">{{ order.doc_no }}</span>
        <el-tag :type="STATUS_TAG[order.status as OutboundStatus]" size="small">
          {{ STATUS_LABELS[order.status as OutboundStatus] ?? order.status }}
        </el-tag>
        <el-tag size="small" type="info">{{ SOURCE_LABELS[order.source as OutboundSource] ?? order.source }}</el-tag>
        <div class="yc-outbd__spacer" />
        <el-button
          v-perm="'wms:outbound:release'"
          v-if="canEdit"
          :loading="busy"
          type="primary"
          @click="act('release', '出库单已释放，可以建波次拣货')"
        >
          释放
        </el-button>
        <el-button
          v-perm="'wms:outbound:wave'"
          v-if="canWave"
          :loading="busy"
          @click="makeWave"
        >
          生成波次
        </el-button>
        <el-popconfirm
          v-if="canShip"
          title="确认发货？将按拣货任务扣减库存并记录出库流水。"
          width="300"
          @confirm="act('ship', '已发货，库存已扣减')"
        >
          <template #reference>
            <el-button v-perm="'wms:outbound:ship'" type="primary">发货</el-button>
          </template>
        </el-popconfirm>
        <el-button
          v-perm="'wms:outbound:close'"
          v-if="canClose"
          :loading="busy"
          @click="act('close', '出库单已关闭')"
        >
          关闭
        </el-button>
        <el-popconfirm
          v-if="canCancel"
          title="确认取消该出库单？"
          width="200"
          @confirm="act('cancel', '出库单已取消')"
        >
          <template #reference>
            <el-button v-perm="'wms:outbound:cancel'" type="danger">取消</el-button>
          </template>
        </el-popconfirm>
      </template>
    </header>

    <div v-if="error" class="yc-outbd__error">
      <TraceIdAlert :error="error" />
    </div>
    <div v-if="notice" class="yc-outbd__notice">{{ notice }}</div>

    <template v-if="detail">
      <el-descriptions class="yc-outbd__meta" :column="4" border>
        <el-descriptions-item label="仓库">
          {{ detail.warehouse_name || `#${order!.warehouse_id}` }}
        </el-descriptions-item>
        <el-descriptions-item label="客户">
          {{ detail.customer_name || `#${order!.customer_id}` }}
        </el-descriptions-item>
        <el-descriptions-item label="期望发货">
          {{ order!.expect_ship_at ? formatDateTime(order!.expect_ship_at) : '—' }}
        </el-descriptions-item>
        <el-descriptions-item label="创建人">{{ order!.created_by }}</el-descriptions-item>
        <el-descriptions-item label="创建时间" :span="2">{{ formatDateTime(order!.created_at) }}</el-descriptions-item>
        <el-descriptions-item label="备注" :span="2">{{ order!.remark || '—' }}</el-descriptions-item>
      </el-descriptions>

      <el-card class="yc-outbd__card">
        <template #header>
          <span>出库明细</span>
          <span v-if="pendingTotal > 0" class="yc-outbd__hint">待拣 {{ pendingTotal }}</span>
        </template>
        <el-table :data="detail.items" row-key="id">
          <el-table-column prop="sku_code" label="商品编码" width="140" />
          <el-table-column prop="sku_name" label="商品名称" min-width="160" show-overflow-tooltip />
          <el-table-column prop="unit" label="单位" width="80" />
          <el-table-column prop="qty" label="应发" width="90" />
          <el-table-column prop="picked_qty" label="已拣" width="90" />
          <el-table-column label="待拣" width="90">
            <template #default="{ row }">
              <span :class="{ 'yc-outbd__pending': row.qty > row.picked_qty }">
                {{ row.qty - row.picked_qty }}
              </span>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </template>
  </div>
</template>

<style scoped lang="scss">
.yc-outbd {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-3);
  }

  &__docno {
    font-weight: 600;
  }

  &__spacer {
    flex: 1;
  }

  &__meta {
    margin-bottom: var(--yc-space-4);
  }

  &__card {
    margin-bottom: var(--yc-space-4);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__notice {
    margin-bottom: var(--yc-space-3);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__hint {
    margin-left: var(--yc-space-2);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }

  &__pending {
    color: var(--el-color-warning);
  }
}
</style>
